import { useContext, useEffect, useRef, useState } from 'react';
import ModalComponent from 'components/Common/Modal';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import useUploadFiles from 'api/hooks/useUploadFiles';
import { SkeletonContent } from 'components/WritingAssistant/ui/MyContent';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import { buildDocumentSharePageV2Url } from 'utils/buildShareDocumentV2Link';
import { getFileBadge } from 'components/UploadHub/fileTypeBadges';
import UploadHubFileDropzone from 'components/UploadHub/UploadHubFileDropzone';
import { DEFAULT_BACKGROUND_COLOUR, DEFAULT_BAR_BACKGROUND_COLOUR } from 'utils/documentPreviewColors';
import { store } from 'components/Store';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { validateUploadFile } from 'utils/uploadFileValidation';

export interface AttachedFile {
  url: string;
  fileName: string;
  contentType: string;
}

type AttachTab = 'myFiles' | 'upload';

interface UploadHubModalAttachProps {
  openState: {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
  };
  onAttach: (files: AttachedFile[]) => void;
  initialFile?: File | null;
}

const UploadHubModalAttach = (props: UploadHubModalAttachProps) => {
  const { state } = useContext(store) as unknown as { state: { team?: { organisationLogo?: string } } };

  const { openState, onAttach, initialFile } = props ?? {};
  const { isOpen, setIsOpen } = openState ?? {};
  const { addItemToUploadHub, getUploadHubList } = useUploadFiles();

  const [teamFiles, setTeamFiles] = useState<UploadHubListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileName, setFileName] = useState('');
  const [isAttaching, setIsAttaching] = useState(false);
  const [activeTab, setActiveTab] = useState<AttachTab>('myFiles');

  const fetchFiles = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchFiles.current = async (signal) => {
    try {
      const results = await getUploadHubList({ signal });
      if (results !== undefined) {
        if (!initialFile) setActiveTab(results.length > 0 ? 'myFiles' : 'upload');
        setTeamFiles(results);
        setIsLoading(false);
      }
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    setIsLoading(true);
    fetchFiles.current?.(controller.signal);
    return () => controller.abort('Files list cancelled.');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !initialFile) return;
    setActiveTab('upload');
    const rejection = validateUploadFile(initialFile);
    if (rejection) createNotification('danger', `${initialFile.name}: ${rejection.message}`);
    setPendingFiles(rejection ? [] : [initialFile]);
    setFileName(rejection ? '' : initialFile.name);
  }, [isOpen, initialFile]);

  const toggleSelected = (url: string) => {
    setSelectedUrls((current) =>
      current.includes(url) ? current.filter((item) => item !== url) : current.concat(url)
    );
  };

  const resetState = () => {
    setPendingFiles([]);
    setSelectedUrls([]);
    setFileName('');
    setActiveTab('myFiles');
  };

  const handleAttach = async () => {
    if (pendingFiles.length === 0 && selectedUrls.length === 0) return;
    setIsAttaching(true);
    try {
      let uploadedFiles: AttachedFile[] = [];
      if (pendingFiles.length > 0) {
        const {
          publicId,
          fileName: createdFileName,
          contentType,
        }: UploadHubListItem = await addItemToUploadHub({
          file: pendingFiles[0],
          fileName,
          organisationLogo: state.team?.organisationLogo,
          color: DEFAULT_BACKGROUND_COLOUR,
          barColor: DEFAULT_BAR_BACKGROUND_COLOUR,
        });
        uploadedFiles = [{ url: buildDocumentSharePageV2Url(publicId), fileName: createdFileName, contentType }];
      }

      const selectedFiles = teamFiles
        .filter((file) => selectedUrls.includes(file.url))
        .map(({ publicId, fileName, contentType }) => ({
          url: buildDocumentSharePageV2Url(publicId),
          fileName,
          contentType,
        }));

      onAttach(uploadedFiles.concat(selectedFiles));
      resetState();
      setIsOpen(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    } finally {
      setIsAttaching(false);
    }
  };

  const attachCount = pendingFiles.length + selectedUrls.length;

  return (
    <ModalComponent isOpen={isOpen} setIsOpen={setIsOpen} onAfterClose={resetState}>
      <div className='p-4'>
        <h3 className='title-h4-bold mb-3'>Attach a file</h3>
      </div>

      <div className='d-flex border-bottom px-4 mb-3'>
        <button
          type='button'
          className={`general-button rounded-top px-4 specific-y-padding-6 mr-2 border border-bottom-none ${
            activeTab === 'myFiles' ? 'bg-main-blue text-white' : 'hovered-button'
          }`}
          onClick={() => setActiveTab('myFiles')}
        >
          My files
        </button>
        <button
          type='button'
          className={`general-button rounded-top px-4 specific-y-padding-6 border border-bottom-none ${
            activeTab === 'upload' ? 'bg-main-blue text-white' : 'hovered-button'
          }`}
          onClick={() => setActiveTab('upload')}
        >
          Upload new file
        </button>
      </div>

      <>
        {activeTab === 'myFiles' ? (
          <CustomScrollbar className='simple-scrollbar' autoHeight autoHeightMin='100%' autoHeightMax='320px'>
            <div className='px-4 pb-4 pt-1' style={{ minHeight: '280px' }}>
              {isLoading ? (
                <SkeletonContent />
              ) : (
                <>
                  {teamFiles.length > 0 ? (
                    <ul className='policy-list px-0 mb-3'>
                      {teamFiles.map((file) => {
                        const { id, url, fileName, contentType } = file;
                        const isSelected = selectedUrls.includes(url);
                        const badge = getFileBadge({ contentType });
                        return (
                          <li className='border-main-blue-50 box-rounded mb-3' key={`upload-hub-attach-${id ?? url}`}>
                            <div
                              className={`d-flex align-items-center justify-content-between px-3 py-2 pointer ${
                                isSelected ? 'rounded bg-blue-hovered border-main-blue' : ''
                              }`}
                              onClick={() => toggleSelected(url)}
                            >
                              <p className='mb-0 flex-centered'>
                                <span
                                  className={`font-weight-bold mr-3  upload-hub-file-icon upload-hub-file-icon-sm ${badge.className} text-white text-center flex-centered justify-content-center`}
                                >
                                  {badge.label}
                                </span>
                                <span className='hidden-lines hidden-one-line'>{fileName}</span>
                              </p>
                              {isSelected && <span className='icon-circle-check  title-h3' />}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <>
                      <p className='text-grey-description mb-3'>{"Your team hasn't uploaded any files yet"}</p>
                    </>
                  )}
                </>
              )}
            </div>
          </CustomScrollbar>
        ) : (
          <div className='pt-3 px-4' style={{ minHeight: '320px' }}>
            <div className='mb-4'>
              <label className='font-weight-bold' htmlFor='uploadHubAttachFileName'>
                File name
              </label>
              <input
                autoFocus={true}
                autoComplete='off'
                id='uploadHubAttachFileName'
                className='main-input form-control'
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
            </div>

            <div className='pb-3'>
              <p className='mb-3 font-weight-bold'>File</p>

              <UploadHubFileDropzone
                pendingFiles={pendingFiles}
                setPendingFiles={setPendingFiles}
                multiple={false}
                onFilesAdded={([file]) => {
                  if (fileName === '') setFileName(file.name);
                }}
              />
            </div>
          </div>
        )}
      </>

      <div className='d-flex justify-content-end p-4 border-top'>
        <button
          className='general-button action-button px-4 specific-y-padding-6 rounded-pill'
          disabled={attachCount === 0 || isAttaching || (pendingFiles.length > 0 && fileName.trim() === '')}
          onClick={handleAttach}
        >
          {isAttaching && <i className='fas fa-spinner fa-spin d-inline-block-centered mr-2' />}
          Attach {attachCount > 0 ? ` ${attachCount} ` : ''}file{attachCount !== 1 ? 's' : ''}
        </button>
      </div>
    </ModalComponent>
  );
};

export default UploadHubModalAttach;
